/**
 * Onboarding option constants — used by the Onboarding flow and preferences panel.
 */

import { BIBLE_TRANSLATIONS } from './bibleBooks'

export const DENOMINATIONS = [
  'Non-denominational',
  'Baptist',
  'Catholic',
  'Methodist',
  'Presbyterian',
  'Lutheran',
  'Pentecostal',
  'Anglican / Episcopal',
  'Other',
]

// TRANSLATIONS is DERIVED from BIBLE_TRANSLATIONS — the single source of
// truth lives in src/data/bibleBooks.js. Add or remove a translation
// there and it propagates automatically to:
//   • Onboarding step 6 dropdown
//   • AccountPage preferences picker
//   • Bible reader's translation menu (via BIBLE_TRANSLATIONS directly)
//   • Chat edge function Scripture citations (reads profile value)
//
// value    = short label ('KJV', 'NKJV') — stored in profiles.preferred_translation
//            and echoed back into the chat system prompt for citations.
// label    = full name + short code, shown in dropdown options.
// disabled = "coming soon" entries (e.g. translations not yet licensed);
//            AccountPage filters these out; onboarding shows them as disabled.
export const TRANSLATIONS = BIBLE_TRANSLATIONS.map(t => ({
  value: t.label,
  label: `${t.full} (${t.label})`,
  disabled: !!t.disabled,
}))

export const MATURITY_OPTIONS = [
  { value: 'new', label: 'New to faith', desc: "I'm just beginning to explore Christianity" },
  { value: 'growing', label: 'Growing', desc: "I'm building my foundation and learning" },
  {
    value: 'established',
    label: 'Established',
    desc: 'I have a solid faith and want to go deeper',
  },
  { value: 'leader', label: 'Leader', desc: 'I teach, mentor, or lead in ministry' },
]

export const ROLE_OPTIONS = [
  { value: 'new_believer', label: 'New Believer', desc: "I'm new to Christianity and exploring" },
  { value: 'growing_christian', label: 'Growing Christian', desc: 'Building my faith day by day' },
  {
    value: 'small_group_leader',
    label: 'Small Group Leader',
    desc: 'I lead or facilitate group Bible studies',
  },
  {
    value: 'pastor_teacher',
    label: 'Pastor / Teacher',
    desc: 'I preach, teach, or shepherd a congregation',
  },
  { value: 'seminary_student', label: 'Seminary Student', desc: "I'm studying theology formally" },
  { value: 'other', label: 'Other', desc: 'None of these quite fit me' },
]

export const PRIMARY_GOAL_OPTIONS = [
  {
    value: 'deepen_faith',
    label: 'Deepen my faith',
    desc: 'Grow in devotion, prayer, and spiritual maturity',
  },
  {
    value: 'understand_bible',
    label: 'Understand the Bible',
    desc: 'Dig into passages, context, and meaning',
  },
  {
    value: 'defend_faith',
    label: 'Defend my faith',
    desc: 'Get equipped for apologetics conversations',
  },
  {
    value: 'sermon_prep',
    label: 'Prepare sermons or studies',
    desc: 'Build outlines, illustrations, and discussion questions',
  },
  {
    value: 'answer_questions',
    label: 'Get answers to tough questions',
    desc: 'Explore theology, doubt, and hard topics',
  },
  {
    value: 'explore_christianity',
    label: 'Explore Christianity',
    desc: "I'm new to the faith and want to learn",
  },
]

export const SCRIPTURE_USE_OPTIONS = [
  {
    value: 'personal_devotion',
    label: 'Personal devotion / quiet time',
    desc: 'Daily reading and reflection on my own',
  },
  {
    value: 'group_study',
    label: 'Group Bible study or small group',
    desc: 'Studying with others in a community setting',
  },
  {
    value: 'preaching',
    label: 'Preaching or teaching',
    desc: 'Preparing messages to share with others',
  },
  {
    value: 'apologetics',
    label: 'Apologetics and faith defense',
    desc: 'Using Scripture to answer tough objections',
  },
  {
    value: 'general_curiosity',
    label: 'General curiosity and learning',
    desc: 'Exploring the Bible out of interest',
  },
]

export const REFERRAL_OPTIONS = [
  { value: 'word_of_mouth', label: 'Word of mouth / friend' },
  { value: 'google_search', label: 'Google search' },
  { value: 'social_media', label: 'Social media (Instagram, TikTok, X)' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'podcast', label: 'Podcast' },
  { value: 'other', label: 'Other' },
]

export const STEP_LABELS = [
  'Background',
  'Purpose',
  'Growth',
  'Scripture',
  'Role',
  'Preferences',
  'Source',
]

// Maps primaryGoal to a pre-filled first prompt stored in sessionStorage
export const GOAL_PROMPTS = {
  deepen_faith: "Help me start a devotional study on a passage that's been on my heart lately.",
  understand_bible: "Can you help me understand a Bible passage I've been wrestling with?",
  defend_faith:
    "What's the best way to respond when someone says the Bible is full of contradictions?",
  sermon_prep: 'Help me build a sermon outline. My passage is...',
  answer_questions:
    "I have a question about my faith that I've been thinking about. Can you help me work through it?",
  explore_christianity:
    "I'm new to Christianity. Can you explain what Christians believe about salvation?",
}

// Update mode: only show the 3 new steps (Purpose, Scripture, Source)
export const UPDATE_STEPS = [2, 4, 7]
export const UPDATE_STEP_LABELS = ['Purpose', 'Scripture', 'Source']
